import moment from 'moment';
import _ from 'underscore';

import {assert} from 'Asserts';
import {Filter, FilterKey, keyToEmptyFilter, Operator, SearchParams} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';
import fuzzydate from 'util/fuzzydate';
import {getDefaultWorklistItemsSearchParams} from 'worklists/WorklistSearchParams';

import {
  mixedWorklistTypeError,
  Worklist,
  WorklistSummariesById,
  WorklistType,
  WorklistTypeToModelTypes,
} from './WorklistConstants';

export function worklistBulkItemsResponseTransform(resp: Array<Worklist>): {[id: string]: Array<string>} {
  const result: {[id: string]: Array<string>} = {};
  for (const worklist of resp) {
    result[worklist.id] = worklist.itemIds;
  }
  return result;
}

export function formatWorklistDate(modifiedAt: string, compact: boolean): string {
  return fuzzydate(modifiedAt, {
    showTime: false,
    compact,
  });
}

export function apiIdToWorklistType(apiId: string): WorklistType | null {
  if (APIUtil.isBatch(apiId)) {
    return WorklistType.BATCH;
  } else if (APIUtil.isContainer(apiId)) {
    return WorklistType.CONTAINER;
  } else if (APIUtil.isBiologicalEntity(apiId)) {
    return WorklistType.BIOENTITY;
  } else if (APIUtil.isPlate(apiId)) {
    return WorklistType.PLATE;
  } else if (APIUtil.isTestOrder(apiId)) {
    return WorklistType.CONTAINER;
  } else {
    return null;
  }
}

export function apiIdHasWorklistType(id: string): boolean {
  return Boolean(apiIdToWorklistType(id));
}

function getValidWorklistTypes(ids: Array<string>): Array<WorklistType> {
  return _.compact(_.unique(ids.map((id) => apiIdToWorklistType(id))));
}

/**
 * Return whether all ids correspond to a single worklist type.
 */
export function apiIdsHaveSameWorklistType(ids: Array<string>): boolean {
  return getValidWorklistTypes(ids).length === 1;
}

/**
 * Return whether all given ids are supported by worklists.
 */
export function allIdsSupported(ids: Array<string>): boolean {
  return ids.every((id) => apiIdHasWorklistType(id));
}

/**
 * Return whether all ids are supported by worklists and that all items correspond to a single worklist type.
 */
export function areIdsValidForWorklist(ids: Array<string>): boolean {
  const allSupported = allIdsSupported(ids);
  const allHaveSameWorklistType = apiIdsHaveSameWorklistType(ids);
  return allSupported && allHaveSameWorklistType;
}

/**
 * Return the worklist type for given ids. Validates ids first.
 */
export function getWorklistType(ids: Array<string>): WorklistType {
  const idsAreValid = areIdsValidForWorklist(ids);
  assert(idsAreValid, `Cannot resolve to a single worklist type for given ids: ${ids.join(', ')}`);
  return getValidWorklistTypes(ids)[0];
}

/**
 * Return tooltip title for the given ids to a worklist
 */
export function idsToTooltipTitle(ids: Array<string>): string {
  const idsValid = areIdsValidForWorklist(ids);
  if (idsValid && ids.length === 1) {
    return 'Add to worklist';
  } else if (idsValid) {
    return 'Add items to worklist';
  } else if (ids.length === 0) {
    return 'Select items to add to worklist';
  } else if (!allIdsSupported(ids)) {
    return 'Some selected items cannot be added to worklists';
  } else if (!apiIdsHaveSameWorklistType(ids)) {
    return mixedWorklistTypeError;
  } else {
    throw new Error(`Must add error message for invalid worklist item ids: [${ids.join(', ')}]`);
  }
}

/**
 * Inject filter to restrict results to only items in a particular worklist into search params.
 * If no search params are passed, returns default search params with item in worklist filter set.
 */
export function resolveItemInWorklistFilter(
  worklistId: string,
  worklistItemType: WorklistType,
  searchParams?: SearchParams
): SearchParams {
  const defaultFilters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: WorklistTypeToModelTypes[worklistItemType],
    },
    {key: FilterKey.ITEM_IN_WORKLIST, value: worklistId, operator: Operator.IS},
  ];

  if (searchParams) {
    const existingFilterKeys = searchParams.filters.map((f) => f.key);
    const filterUnion = [
      ...searchParams.filters,
      ...defaultFilters.filter((f) => !existingFilterKeys.includes(f.key)),
    ];
    return {...searchParams, filters: filterUnion};
  }

  return {
    ...getDefaultWorklistItemsSearchParams(),
    filters: defaultFilters,
  };
}

/**
 * Given worklist summaries, returns their ids sorted from most recent to least.
 */
export function mostRecentIds(worklistSummaries: WorklistSummariesById): Array<string> {
  return _.sortBy(Object.values(worklistSummaries), (w) => moment(w.modifiedAt))
    .reverse()
    .map((w) => w.id);
}
